import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import React from "react";

import { ProductFilters } from "../../types/productPlane";
import ExploreFilterPanel from "../Explore/ExploreFilterPanel";
import ExploreFilterPanelPlaceholder from "../Explore/ExploreFilterPanelPlaceholder";
import ExploreSearchField from "../Explore/ExploreSearchField";
import {
  ExploreNarrow,
  PRODUCT_NOUN,
  PRODUCT_SEARCH_PLACEHOLDER,
  unnamedLabel,
} from "../Explore/exploreFilters";
import { AnalyticsSelection } from "./analyticsState";

export interface ScopeFiltersProps {
  // The selection the page reads over: what is picked is read from it
  selection: AnalyticsSelection;
  onChange: (change: Partial<AnalyticsSelection>) => void;
  // The filters of the product search over the selection, each value with
  // its count; null until the first answer
  filters: ProductFilters | null;
  // Whether the search for them failed
  failed: boolean;
  // The text typed into the box of one long filter; null for none
  narrow: ExploreNarrow | null;
  onNarrowChange: (narrow: ExploreNarrow | null) => void;
  // Called when the rest of a cut list is asked for
  onShowAll: () => void;
}

// What the scope is changed by, on the page itself: the keyword, the switch
// for the products without a name and Explore's filters, laid out in a row.
// A change is a change of the selection, so every view reads anew.
const ScopeFilters: React.FC<ScopeFiltersProps> = ({
  selection,
  onChange,
  filters,
  failed,
  narrow,
  onNarrowChange,
  onShowAll,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 2,
        rowGap: 1,
      }}
    >
      <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
        <ExploreSearchField
          keyword={selection.keyword}
          onSubmit={(keyword) => onChange({ keyword })}
          subject={PRODUCT_NOUN}
          placeholder={PRODUCT_SEARCH_PLACEHOLDER}
        />
      </Box>
      <FormControlLabel
        label={
          <Typography variant="body2" color="text.secondary">
            {unnamedLabel(PRODUCT_NOUN)}
          </Typography>
        }
        control={
          <Switch
            size="small"
            checked={selection.unnamed}
            onChange={(event) => onChange({ unnamed: event.target.checked })}
          />
        }
        sx={{ mr: 0 }}
      />
    </Box>

    {filters ? (
      <ExploreFilterPanel
        layout="row"
        filters={filters}
        state={selection}
        onChange={onChange}
        narrow={narrow}
        onNarrowChange={onNarrowChange}
        onShowAll={onShowAll}
      />
    ) : failed ? (
      <Alert severity="error">The filters could not be loaded.</Alert>
    ) : (
      <ExploreFilterPanelPlaceholder layout="row" />
    )}
  </Box>
);

export default ScopeFilters;
